import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminApiService, ClientListItem, LeaderboardEntry } from '../services/admin-api.service';

@Component({
  selector: 'app-admin-leaderboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <div class="page-header">
        <div>
          <h2>🏆 Leaderboard</h2>
          <p>Gestiona las entradas del leaderboard</p>
        </div>
        <div class="header-actions">
          <button class="btn btn-danger" *ngIf="filterClient || filterGame"
            (click)="confirmClear = true">Limpiar filtrados</button>
          <button class="btn btn-danger" (click)="confirmClearAll = true">Limpiar todo</button>
        </div>
      </div>

      <div class="message success" *ngIf="message">{{ message }}</div>
      <div class="message error" *ngIf="error">{{ error }}</div>

      <!-- Confirm dialogs -->
      <div class="confirm-bar" *ngIf="confirmClear">
        ⚠️ Esto eliminará las entradas filtradas actualmente.
        <button class="btn btn-sm" (click)="confirmClear = false">Cancelar</button>
        <button class="btn btn-sm btn-danger" (click)="clearFiltered()">Confirmar</button>
      </div>
      <div class="confirm-bar" *ngIf="confirmClearAll">
        ⚠️ Esto eliminará TODAS las entradas del leaderboard.
        <button class="btn btn-sm" (click)="confirmClearAll = false">Cancelar</button>
        <button class="btn btn-sm btn-danger" (click)="clearAll()">Confirmar</button>
      </div>

      <!-- Filters -->
      <div class="filters">
        <select [(ngModel)]="filterClient" (ngModelChange)="load()" class="input select">
          <option value="">Todos los clientes</option>
          <option *ngFor="let c of clients" [value]="c.code">{{ c.code }} — {{ c.client_name }}</option>
        </select>
        <select [(ngModel)]="filterGame" (ngModelChange)="load()" class="input select">
          <option value="">Todos los juegos</option>
          <option value="memory">🧠 Memory</option>
          <option value="puzzle">🧩 Puzzle</option>
          <option value="reaction">⚡ Reaction</option>
          <option value="simon">🎵 Simon</option>
          <option value="whack">🎯 Whack</option>
          <option value="speedtap">👆 SpeedTap</option>
          <option value="balloonpop">🎈 BalloonPop</option>
        </select>
        <div class="search-box">
          <input [(ngModel)]="searchTerm" placeholder="Buscar jugador..." class="input"
            (keydown.enter)="load()" />
          <button class="btn btn-sm" (click)="load()">🔍</button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-card">
        <div class="empty-state" *ngIf="entries.length === 0 && !loading">
          <span>🏆</span>
          <p>No hay entradas</p>
        </div>
        <div class="loading" *ngIf="loading">Cargando...</div>
        <table *ngIf="entries.length > 0">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Juego</th>
              <th>Jugador</th>
              <th>RUT</th>
              <th>Puntaje</th>
              <th>Fecha</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let e of entries">
              <td><span class="badge">{{ e.client_code }}</span></td>
              <td><span class="badge game-badge">{{ e.game_id }}</span></td>
              <td class="bold">{{ e.player_name }}</td>
              <td class="mono muted">{{ e.player_rut || '-' }}</td>
              <td class="mono bold">{{ e.score }}</td>
              <td class="muted small">{{ e.created_at | date:'short' }}</td>
              <td>
                <button class="btn-icon" (click)="deleteEntry(e.id)" title="Eliminar">🗑️</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `,
  styles: [`
    .page { max-width: 1200px; }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;
    }

    .page-header h2 { font-size: 24px; font-weight: 700; color: #1e293b; margin: 0; }
    .page-header p { color: #64748b; margin: 4px 0 0; font-size: 14px; }

    .header-actions { display: flex; gap: 8px; }

    .filters {
      display: flex;
      gap: 12px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }

    .select { width: auto; min-width: 200px; }

    .search-box { display: flex; gap: 4px; }
    .search-box .input { width: 220px; }

    .table-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }

    thead tr {
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }

    th {
      text-align: left;
      padding: 12px 16px;
      font-weight: 600;
      color: #64748b;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    td {
      padding: 10px 16px;
      border-bottom: 1px solid #f1f5f9;
    }

    tr:hover td { background: #f8fafc; }

    .badge {
      font-size: 11px;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 6px;
      background: #f1f5f9;
      color: #475569;
      font-family: monospace;
    }

    .game-badge { background: #eef2ff; color: #4338ca; }

    .bold { font-weight: 600; color: #1e293b; }
    .mono { font-family: monospace; }
    .muted { color: #94a3b8; }
    .small { font-size: 12px; }

    .btn-icon {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      font-size: 14px;
      opacity: 0.5;
      transition: opacity 0.15s;
    }

    .btn-icon:hover { opacity: 1; }

    .empty-state { text-align: center; padding: 48px; color: #94a3b8; }
    .empty-state span { font-size: 40px; display: block; margin-bottom: 8px; }
    .loading { text-align: center; padding: 32px; color: #94a3b8; }

    .confirm-bar {
      background: #fffbeb;
      border: 1px solid #fed7aa;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
      color: #92400e;
    }

    .btn {
      padding: 8px 16px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #fff;
      color: #334155;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s;
    }

    .btn:hover { background: #f1f5f9; }
    .btn-sm { padding: 6px 12px; font-size: 12px; }

    .btn-danger {
      color: #dc2626;
      border-color: #fecaca;
      background: #fff;
    }

    .btn-danger:hover { background: #fef2f2; }

    .input {
      padding: 9px 12px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
      box-sizing: border-box;
    }

    .input:focus { border-color: #4338ca; }

    .message {
      padding: 12px 16px;
      border-radius: 8px;
      margin-bottom: 16px;
      font-size: 14px;
    }

    .message.success { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
    .message.error { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
  `]
})
export class LeaderboardComponent implements OnInit {
  entries: LeaderboardEntry[] = [];
  clients: ClientListItem[] = [];
  loading = true;
  filterClient = '';
  filterGame = '';
  searchTerm = '';
  message = '';
  error = '';
  confirmClear = false;
  confirmClearAll = false;

  constructor(private api: AdminApiService) {}

  ngOnInit() {
    this.api.getClients().subscribe({
      next: (data: any) => this.clients = data.results || data,
    });
    this.load();
  }

  load() {
    this.loading = true;
    const params: any = {};
    if (this.filterClient) params.client = this.filterClient;
    if (this.filterGame) params.game_id = this.filterGame;
    if (this.searchTerm) params.search = this.searchTerm;

    this.api.getLeaderboard(params).subscribe({
      next: (data: any) => {
        this.entries = data.results || data;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar leaderboard';
        this.loading = false;
      }
    });
  }

  deleteEntry(id: number) {
    this.api.deleteLeaderboardEntry(id).subscribe({
      next: () => {
        this.entries = this.entries.filter(e => e.id !== id);
        this.message = 'Entrada eliminada';
        setTimeout(() => this.message = '', 3000);
      },
      error: () => { this.error = 'Error al eliminar'; }
    });
  }

  clearFiltered() {
    const params: any = {};
    if (this.filterClient) params.client = this.filterClient;
    if (this.filterGame) params.game_id = this.filterGame;
    this.api.clearLeaderboard(params).subscribe({
      next: (res) => {
        this.confirmClear = false;
        this.message = `${res.deleted} entradas eliminadas`;
        this.load();
        setTimeout(() => this.message = '', 3000);
      },
      error: () => { this.error = 'Error al limpiar'; }
    });
  }

  clearAll() {
    this.api.clearLeaderboard().subscribe({
      next: (res) => {
        this.confirmClearAll = false;
        this.message = `${res.deleted} entradas eliminadas`;
        this.load();
        setTimeout(() => this.message = '', 3000);
      },
      error: () => { this.error = 'Error al limpiar'; }
    });
  }
}
